import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { isOperatorAuthenticated, subscribeAuth } from "../lib/auth";
import { Btn } from "../lib/Btn";
import { Icon } from "../lib/Icon";
import { LanguageSwitcher } from "../lib/LanguageSwitcher";
import { RULogo } from "../lib/Logo";
import { Slats } from "../lib/Slats";
import { ThemeToggle } from "../lib/ThemeToggle";
import { cn } from "../lib/cn";
import { captureUtmFromLocation } from "../lib/utm";
import { registerCampaign } from "../lib/telemetry";

const links = [
  { to: "/", label: "Home" },
  { to: "/product", label: "Product" },
  { to: "/verticals", label: "Verticals" },
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/docs", label: "Docs" },
  { to: "/book-demo", label: "Book a demo", tone: "accent" as const },
  { to: "/pilot", label: "Pilot", tone: "accent" as const },
  { to: "/tools", label: "Tools" },
  { to: "/vs", label: "vs" },
  { to: "/states", label: "States" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export function MarketingShell() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const [authed, setAuthed] = useState(() => isOperatorAuthenticated());
  useEffect(
    () => subscribeAuth(() => setAuthed(isOperatorAuthenticated())),
    [],
  );
  // First-touch UTM capture on every marketing route. Idempotent —
  // if the URL has no UTM params, this is a cheap read from
  // localStorage that returns the previously-captured bundle.
  // Also decodes `?_utm=<base64>` handoff blobs from the app-side.
  useEffect(() => {
    // Register with PostHog too, so the campaign rides on events + the person
    // record (not just the anonymous auto-captured pageview) and survives the
    // handoff to the identified operator.
    registerCampaign(captureUtmFromLocation());
  }, [location.pathname, location.search]);
  return (
    <div className="min-h-screen flex flex-col bg-bg text-ink">
      {/* Skip-to-content link — invisible until focused. First
          keyboard-tab lands here so screen-reader users don't have
          to walk the whole nav before reaching the page content.
          Also picked up by Google's a11y crawler as a positive
          accessibility signal. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-3 focus:py-2 focus:bg-accent focus:text-on-accent focus:rounded-md focus:font-semibold"
      >
        Skip to content
      </a>
      <header className="sticky top-0 z-20 bg-bg/85 backdrop-blur-sm border-b border-line">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 flex items-center justify-between h-[68px]">
          <Link to="/" className="shrink-0">
            <RULogo size={28} wordSize={22} />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  cn(
                    "px-[14px] py-2 rounded-md text-[13.5px] font-semibold",
                    isActive
                      ? "bg-ink text-bg"
                      : l.tone === "accent"
                      ? "text-accent hover:bg-paper"
                      : "text-ink hover:bg-paper",
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-2">
            <ThemeToggle />
            {authed ? (
              <Link to="/app/today">
                <Btn kind="primary" size="sm" iconRight={<Icon name="arrow-right" size={14} />}>
                  Open dashboard
                </Btn>
              </Link>
            ) : (
              <>
                <Link to="/signin">
                  <Btn kind="ghost" size="sm">
                    Sign in
                  </Btn>
                </Link>
                <Link to="/signup">
                  <Btn kind="primary" size="sm">
                    Start free
                  </Btn>
                </Link>
              </>
            )}
          </div>
          <button
            type="button"
            className="md:hidden inline-grid place-items-center w-9 h-9 rounded-md border border-line bg-paper"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            <Icon name={open ? "close" : "menu"} size={18} />
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-line bg-paper px-5 py-4 flex flex-col gap-2">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={cn(
                  "py-2 text-[15px] font-semibold",
                  location.pathname === l.to ? "text-accent" : "text-ink",
                )}
              >
                {l.label}
              </Link>
            ))}
            <div className="flex gap-2 mt-2">
              {authed ? (
                <Link to="/app/today" className="flex-1" onClick={() => setOpen(false)}>
                  <Btn kind="primary" size="md" full>
                    Open dashboard
                  </Btn>
                </Link>
              ) : (
                <>
                  <Link to="/signin" className="flex-1" onClick={() => setOpen(false)}>
                    <Btn kind="ghost" size="md" full>
                      Sign in
                    </Btn>
                  </Link>
                  <Link to="/signup" className="flex-1" onClick={() => setOpen(false)}>
                    <Btn kind="primary" size="md" full>
                      Start free
                    </Btn>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-surface border-t border-line mt-16">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12 grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]">
          <div>
            <RULogo size={28} wordSize={22} />
            <p className="text-sub text-sm mt-4 max-w-[260px]">
              Property operations for multifamily, self-storage, parking, and
              RV/boat operators of every size. Flat from $59/month, 0% of your
              tenant rent. No partner-integration bloat.
            </p>
            <Slats height={14} bands={5} color="var(--accent)" style={{ width: 88, marginTop: 18 }} />
          </div>
          <FooterCol
            title="Product"
            items={[
              { label: "Features", to: "/features" },
              { label: "Product overview", to: "/product" },
              { label: "Pricing", to: "/pricing" },
              { label: "Documentation", to: "/docs" },
              { label: "Free tools", to: "/tools" },
              { label: "Customers", to: "/customers" },
              { label: "Sign up", to: "/signup" },
            ]}
          />
          <FooterCol
            title="Coverage"
            items={[
              { label: "All 50 states + DC", to: "/states" },
              { label: "Migrate from storEDGE", to: "/migrate-from-storedge" },
              { label: "Keep 100% of your rent", to: "/keep-100-of-your-rent" },
              { label: "vs storEDGE, SiteLink, Cubby", to: "/vs" },
              { label: "Sales tax · operator remits", to: "/states" },
            ]}
          />
          <FooterCol
            title="Company"
            items={[
              { label: "Contact", to: "/contact" },
              { label: "Privacy", to: "/privacy" },
              { label: "Terms", to: "/terms" },
              { label: "info@nestplot.com", to: "mailto:info@nestplot.com" },
            ]}
          />
          <FooterCol
            title="Support"
            items={[
              { label: "1-hour response · Mon–Sat 9–6 ET", to: "/contact" },
              { label: "All systems operational", to: "#" },
            ]}
            highlight
          />
        </div>
        <div className="border-t border-line">
          <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-5 text-sub font-mono text-[11.5px] uppercase tracking-[0.1em] flex flex-col md:flex-row justify-between gap-2">
            <span className="flex items-center gap-4">
              <span>© {new Date().getFullYear()} NestPlot</span>
              <LanguageSwitcher className="normal-case tracking-normal" />
            </span>
            <span className="flex items-center gap-4">
              <a href="mailto:info@nestplot.com" className="hover:text-ink">info@nestplot.com</a>
              {/* The Page existed with nothing pointing at it. */}
              <a
                href="https://www.linkedin.com/company/nestplot"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="NestPlot on LinkedIn"
                className="inline-flex items-center gap-1.5 hover:text-ink"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.22.79 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
                </svg>
                LinkedIn
              </a>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

interface FooterColProps {
  title: string;
  items: { label: string; to: string }[];
  highlight?: boolean;
}

function FooterCol({ title, items, highlight }: FooterColProps) {
  return (
    <div>
      <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase mb-4">
        {title}
      </div>
      <ul className="flex flex-col gap-2 text-sm">
        {items.map((i) => (
          <li key={i.label}>
            {i.to.startsWith("/") ? (
              <Link to={i.to} className="text-ink hover:text-accent">
                {highlight && <span className="inline-block w-[6px] h-[6px] rounded-full bg-ok mr-2" />}
                {i.label}
              </Link>
            ) : (
              <a href={i.to} className="text-ink hover:text-accent">
                {highlight && <span className="inline-block w-[6px] h-[6px] rounded-full bg-ok mr-2" />}
                {i.label}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
